import { useState, useCallback, useEffect, useRef } from "react";
import {
  Presentation,
  ChevronLeft,
  ChevronRight,
  MessageSquarePlus,
  ListChecks,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import type { SlidesData } from "@/lib/chat/parseSlidesBlock";

interface SlidesProps {
  data: SlidesData;
}

export function Slides({ data }: SlidesProps) {
  const [index, setIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const total = data.slides.length;
  const slide = data.slides[index];

  const goPrev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);
  const goNext = useCallback(
    () => setIndex((i) => Math.min(total - 1, i + 1)),
    [total]
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onKey = (e: KeyboardEvent) => {
      if (!el.contains(document.activeElement)) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
      }
    };
    el.addEventListener("keydown", onKey as any);
    return () => el.removeEventListener("keydown", onKey as any);
  }, [goPrev, goNext]);

  const prefill = (text: string) => {
    window.dispatchEvent(new CustomEvent("chat:prefill", { detail: { text } }));
  };

  const progress = ((index + 1) / total) * 100;

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      className="my-4 w-full rounded-3xl border bg-card shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
      role="group"
      aria-label={`Slides: ${data.title}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-3 border-b px-5 py-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-foreground">{data.title}</p>
          <p className="text-xs text-muted-foreground">Slides</p>
        </div>
        <span className="shrink-0 rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground tabular-nums">
          {index + 1} / {total}
        </span>
      </div>

      {/* Progress */}
      <div className="h-1 w-full bg-muted">
        <div
          className="h-full bg-primary transition-all duration-300 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Stage */}
      <div className="px-6 py-6">
        <p className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Slide {index + 1}
        </p>
        <h3 className="mb-3 text-lg font-medium leading-snug text-foreground">
          {slide.title}
        </h3>
        {slide.body && (
          <p className="text-sm leading-7 text-foreground/90">{slide.body}</p>
        )}
        {slide.bullets && slide.bullets.length > 0 && (
          <ul className="mt-3 space-y-1.5">
            {slide.bullets.map((b, i) => (
              <li
                key={i}
                className="flex gap-2 text-sm leading-7 text-foreground/90"
              >
                <span
                  className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/70"
                  aria-hidden="true"
                />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        )}
        {slide.keyFact && (
          <div className="mt-4 rounded-xl border bg-muted/50 p-3">
            <p className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {slide.keyFact.label}
            </p>
            <p className="text-sm text-foreground/90">{slide.keyFact.content}</p>
          </div>
        )}
      </div>

      {/* Nav */}
      <div className="flex items-center justify-between gap-3 border-t px-4 py-3">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={goPrev}
          disabled={index === 0}
          aria-label="Previous slide"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          <span className="ml-1">Prev</span>
        </Button>
        <div className="flex items-center gap-1.5" role="tablist">
          {data.slides.map((_, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => setIndex(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? "w-5 bg-primary" : "w-1.5 bg-muted-foreground/30 hover:bg-muted-foreground/50"
              }`}
            />
          ))}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={goNext}
          disabled={index === total - 1}
          aria-label="Next slide"
        >
          <span className="mr-1">Next</span>
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>

      {/* Footer actions */}
      <div className="flex flex-wrap items-center gap-2 border-t px-4 py-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-full"
          onClick={() =>
            prefill(`Tell me more about slide ${index + 1}: "${slide.title}"`)
          }
        >
          <MessageSquarePlus className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
          Ask about this slide
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-full"
          onClick={() => prefill(`Summarize all slides on "${data.title}"`)}
        >
          <ListChecks className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
          Summarize all slides
        </Button>
      </div>
    </div>
  );
}
